/**
 * SlideshowClipSource — turns a video FILE into the two things the slideshow
 * needs from it, and hides which engine delivered them:
 *
 *   preview  — a plain <video> playing in sync with the timeline. Browsers do
 *              A/V sync better than any hand-rolled clock, so preview never
 *              goes through a decoder we wrote.
 *   export   — `frameAt(tSec)` → a drawable for the exact frame at that time,
 *              walked MONOTONICALLY (which is how the encoder walks time).
 *
 * Two export engines, picked per file, best first:
 *
 *   A. WebCodecs — the lazy sample table + hardware VideoDecoder, via the
 *      existing VideoMergeWebCodecs DecodeStream/ClipCursor (the same pair that
 *      drives /music-video-maker). Frames arrive in decode order at hardware
 *      speed; no seeking at all. MP4-family through mp4box, and since
 *      22.10.2026 WebM/MKV (VP8, VP9, AV1, H.264) through the module's Matroska
 *      table (demuxClip with `matroska`) — the door /video-bg-remover,
 *      /video-editor and /video-trim already went through.
 *
 *   B. <video> seek — universal fallback for what neither table opens (avi,
 *      HEVC in mkv, MPEG-4 Part 2) or codecs VideoDecoder rejects. The header
 *      used to say 24–60 ms per frame; measured 22.10.2026 on a 1080p VP8 WebM
 *      in Chromium: 230–280 ms median, up to 585 ms — 3–4 frames a second, so
 *      a 12-second slide of it is ~100 s of export.
 *
 * ⚠️ Engine B must wait for the frame to be PRESENTED, not merely for `seeked`.
 * `seeked` fires when the seek completes; drawing right after it grabs the
 * PREVIOUS frame. Measured on the colour-bar fixture: the 1 s mark came out
 * red instead of green — and only in a HEADED browser, because headless
 * without a compositor happened to be slow enough to hide it. requestVideo-
 * FrameCallback is the correct wait; it does not fire for a paused video in a
 * non-compositing (headless / fully-backgrounded) tab, so there is a timer
 * fallback behind it.
 */
(function () {
    'use strict';

    function ssmTrack(name, props, slug) {
        if (window.__ae) window.__ae('feature_use', Object.assign({ slug: slug || 'slideshow-maker', f: name }, props || {}));
    }

    const VIDEO_EXT = /\.(mp4|m4v|mov|webm|mkv|avi|ogv|3gp|m2ts|mts)$/i;

    function isVideoFile(f) {
        if (!f) return false;
        const t = (f.type || '').toLowerCase();
        if (t.indexOf('video/') === 0) return true;
        return VIDEO_EXT.test(f.name || '');
    }

    /**
     * Metadata + a poster frame, read through a plain <video>. Universal: this
     * is also the honest capability probe — if <video> cannot open the file we
     * can still fall through to the WebCodecs engine, and only when BOTH fail
     * is the clip genuinely unusable.
     */
    function probe(file, posterAtSec) {
        return new Promise(function (resolve) {
            const url = URL.createObjectURL(file);
            const v = document.createElement('video');
            let settled = false;
            const done = function (res) {
                if (settled) return;
                settled = true;
                clearTimeout(guard);
                try { URL.revokeObjectURL(url); } catch (_) {}
                try { v.removeAttribute('src'); v.load(); } catch (_) {}
                try { v.remove(); } catch (_) {}
                resolve(res);
            };
            const guard = setTimeout(function () {
                done({ ok: false, reason: 'timeout', duration: 0, width: 0, height: 0, poster: '' });
            }, 20000);
            v.muted = true;
            v.playsInline = true;
            v.preload = 'metadata';
            // In the DOM (tiny + transparent): requestVideoFrameCallback does
            // not fire for a detached element, and the poster grab needs it.
            v.style.cssText = 'position:fixed;left:0;bottom:0;width:2px;height:2px;opacity:.01;pointer-events:none';
            v.onerror = function () {
                done({ ok: false, reason: 'decode', duration: 0, width: 0, height: 0, poster: '' });
            };
            v.onloadedmetadata = function () {
                const dur = isFinite(v.duration) ? v.duration : 0;
                const w = v.videoWidth || 0;
                const h = v.videoHeight || 0;
                if (!dur || !w) { done({ ok: false, reason: 'empty', duration: dur, width: w, height: h, poster: '' }); return; }
                const at = Math.min(Math.max(0, posterAtSec != null ? posterAtSec : dur * 0.1), Math.max(0, dur - 0.05));
                const grab = function () {
                    let poster = '';
                    try {
                        const c = document.createElement('canvas');
                        const scale = Math.min(1, 320 / Math.max(1, w));
                        c.width = Math.max(1, Math.round(w * scale));
                        c.height = Math.max(1, Math.round(h * scale));
                        c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
                        poster = c.toDataURL('image/jpeg', 0.62);
                    } catch (_) {}
                    done({ ok: true, duration: dur, width: w, height: h, poster: poster });
                };
                v.onseeked = function () { waitPresented(v, grab); };
                try { v.currentTime = at; }
                catch (_) { done({ ok: true, duration: dur, width: w, height: h, poster: '' }); }
            };
            v.src = url;
            document.body.appendChild(v);
        });
    }

    // Wait until the seeked frame is actually on screen. See the header note.
    function waitPresented(v, cb) {
        let fired = false;
        const go = function () { if (!fired) { fired = true; cb(); } };
        if (v.requestVideoFrameCallback) {
            try { v.requestVideoFrameCallback(function () { go(); }); } catch (_) {}
        }
        // Backstop for tabs that never composite (headless, fully backgrounded):
        // rVFC simply never fires there, and without this the export would hang.
        setTimeout(go, 90);
    }

    /**
     * Can the first bytes of this file be read at all? A cloud-placeholder
     * file (OneDrive/Google Drive on-demand on Windows) reports its size
     * instantly but fetches bytes over the network on first read — on a slow
     * link that read can stall far past any probe timeout, and the honest
     * message is "файл недоступен", not "попробуйте другой формат". 16 bytes
     * is enough to tell "stalls/errors" from "reads fine".
     */
    function sniffReadable(file, timeoutMs) {
        return new Promise(function (resolve) {
            let done = false;
            const fin = function (ok) { if (!done) { done = true; resolve(ok); } };
            const guard = setTimeout(function () { fin(false); }, timeoutMs || 8000);
            try {
                const fr = new FileReader();
                fr.onload = function () { clearTimeout(guard); fin(true); };
                fr.onerror = function () { clearTimeout(guard); fin(false); };
                fr.readAsArrayBuffer(file.slice(0, 16));
            } catch (_) { clearTimeout(guard); fin(false); }
        });
    }

    /**
     * Metadata WITHOUT a media decoder: mp4box reads dims + duration straight
     * from the container. This is the tier that rescues files the <video>
     * probe rejects or times out on — /music-video-maker had the same trap
     * (err_clip_decode was its #1 error bucket, 178 events/30 d) until it grew
     * exactly this ladder; the probe being flaky under memory pressure is how
     * a REAL user (2026-08-06, 4 GB YaBrowser, 172-photo deck) lost her clip.
     * MP4-family only; guarded by the ftyp sniff mp4box requires (it walks
     * non-ISOBMFF bytes synchronously for minutes — measured in mvm).
     */
    async function probeViaMp4box(file) {
        const VMW = window.VideoMergeWebCodecs;
        if (!VMW || typeof VMW.demux !== 'function') return null;
        if (typeof VMW.isMp4Family === 'function' && !VMW.isMp4Family(file)) return null;
        try {
            if (typeof VMW.loadMp4Box === 'function') await VMW.loadMp4Box();
            const d = await Promise.race([
                VMW.demux(file, true),
                new Promise(function (_, rej) { setTimeout(function () { rej(new Error('mp4box-timeout')); }, 12000); }),
            ]);
            const inf = d && d.info;
            let dur = 0;
            if (inf && inf.duration && inf.timescale) dur = inf.duration / inf.timescale;
            if (!dur && inf && inf.fragment_duration && inf.timescale) dur = inf.fragment_duration / inf.timescale;
            const vt = d && d.vtrack;
            if (dur > 0.05 && vt && vt.video) {
                // display-, не coded-размер: портретная съёмка с телефона хранится лёжа
                return { ok: true, duration: dur, width: d.displayWidth || vt.video.width || 0,
                    height: d.displayHeight || vt.video.height || 0, poster: '' };
            }
        } catch (_) {}
        return null;
    }

    /**
     * The full admission ladder. Only when EVERY tier fails is the clip
     * rejected — and then with a reason that names the actual problem:
     *   'unreadable' — the bytes themselves cannot be read (cloud placeholder,
     *                  vanished media) — no format advice will help
     *   'codec'      — readable, but nothing here can open it
     */
    async function probeLadder(file) {
        const readable = await sniffReadable(file, 8000);
        if (!readable) {
            ssmTrack('clip_probe_failed', { reason: 'unreadable', ext: extOf(file), mb: mbOf(file) });
            return { ok: false, reason: 'unreadable' };
        }
        // Through the export, not the closure — so a caller (or a test forcing
        // the flaky-probe case) that swaps `SlideshowClipSource.probe` is
        // actually exercised by the ladder. And with the ladder's own leash on
        // top of the probe's internal guard: a probe that neither resolves nor
        // rejects (decoder wedged under memory pressure) must fall through to
        // the next tier, not hang the whole admission.
        const probe1 = (window.SlideshowClipSource && window.SlideshowClipSource.probe) || probe;
        const p1 = await Promise.race([
            probe1(file).catch(function () { return null; }),
            new Promise(function (r) { setTimeout(function () { r(null); }, 22000); }),
        ]);
        if (p1 && p1.ok) return Object.assign({ tier: 'video' }, p1);
        const p2 = await probeViaMp4box(file);
        if (p2 && p2.ok) {
            ssmTrack('clip_probe_fallback', { tier: 'mp4box', ext: extOf(file), r1: (p1 && p1.reason) || 'x' });
            return Object.assign({ tier: 'mp4box' }, p2);
        }
        ssmTrack('clip_probe_failed', { reason: 'codec:' + ((p1 && p1.reason) || 'x'), ext: extOf(file), mb: mbOf(file) });
        return { ok: false, reason: 'codec' };
    }

    function extOf(f) { return (((f && f.name) || '').split('.').pop() || '').toLowerCase().slice(0, 5); }
    function mbOf(f) { return Math.round(((f && f.size) || 0) / 1048576); }

    /**
     * Poster stand-in for a clip whose pixels cannot be decoded right now
     * (ladder tier 2, or a trusted restore whose probe timed out). A visible
     * film tile — never a black hole, never a dropped slide.
     */
    function placeholderPoster(w, h) {
        const c = document.createElement('canvas');
        c.width = 320; c.height = Math.max(60, Math.round(320 * ((h || 9) / (w || 16))));
        const g = c.getContext('2d');
        g.fillStyle = '#1c2333';
        g.fillRect(0, 0, c.width, c.height);
        g.fillStyle = 'rgba(255,255,255,.85)';
        const cx = c.width / 2, cy = c.height / 2, r = Math.min(c.width, c.height) * 0.18;
        g.beginPath();
        g.moveTo(cx - r * 0.6, cy - r);
        g.lineTo(cx + r, cy);
        g.lineTo(cx - r * 0.6, cy + r);
        g.closePath();
        g.fill();
        return c.toDataURL('image/png');
    }

    /** A <video> for live preview. Kept per slide, reused across plays. */
    function createPreviewElement(file) {
        const v = document.createElement('video');
        v.src = URL.createObjectURL(file);
        v.preload = 'auto';
        v.playsInline = true;
        v.muted = true;              // the mixer owns clip sound; see export
        v.crossOrigin = 'anonymous';
        v.style.cssText = 'position:fixed;left:0;bottom:0;width:2px;height:2px;opacity:.01;pointer-events:none';
        document.body.appendChild(v);
        return v;
    }

    function releasePreviewElement(v) {
        if (!v) return;
        try { v.pause(); } catch (_) {}
        try { URL.revokeObjectURL(v.src); } catch (_) {}
        try { v.removeAttribute('src'); v.load(); } catch (_) {}
        try { v.remove(); } catch (_) {}
    }

    /* ── Export engines ──────────────────────────────────────────────── */

    async function openWebCodecsEngine(file, fps, startSec, deferRotation, slug) {
        const VMW = window.VideoMergeWebCodecs;
        if (!VMW || typeof VMW.demux !== 'function' || typeof window.VideoDecoder === 'undefined') return null;
        /* WebM/MKV — the same lazy table, read by the module's Matroska walker.
           Until 22.10.2026 this engine refused every non-mp4 file, so a WebM clip
           went to the per-frame <video> seek — and on 21.10 that clip hung two
           exports of a 20-slide deck in Yandex Browser (comment #1978; the export
           stood on the WebM slide until the 3-minute guard ended it as «Экспорт не
           удался»). */
        const mkv = typeof VMW.isMp4Family === 'function' && !VMW.isMp4Family(file)
            && typeof VMW.isMatroska === 'function' && VMW.isMatroska(file) && typeof VMW.demuxClip === 'function';
        if (!mkv && typeof VMW.isMp4Family === 'function' && !VMW.isMp4Family(file)) return null;
        let d;
        try {
            if (mkv) {
                d = await VMW.demuxClip(file, false, { matroska: true, slug: slug || 'slideshow-maker' });
            } else {
                if (typeof VMW.loadMp4Box === 'function') await VMW.loadMp4Box();
                /* The sample TABLE now, the bytes on demand. The eager demux() reads
                   the whole clip into an ArrayBuffer, lets mp4box keep its own copy
                   and then stores every sample's bytes in an array — measured in
                   Yandex Browser on a 182 MB 1080p clip: +483 MB of working set in
                   609 ms, i.e. 2.65x the file, on the main thread, before the first
                   frame. demuxLazy on the same clip: +38 MB in 19 ms. DecodeStream
                   already reads lazy samples in coalesced runs (the path
                   /video-insert exports through), so nothing downstream changes.
                   exportBegin opens EVERY clip of the deck up front, so the eager
                   cost was multiplied by the clip count. */
                d = (typeof VMW.demuxLazy === 'function') ? await VMW.demuxLazy(file) : await VMW.demux(file);
            }
        } catch (_) { return null; }
        if (!d || !d.samples || !d.samples.length || !d.vtrack) return null;
        try {
            /* The module's one config shape: a stream without a description
               (VP8/VP9/AV1) must OMIT the key — a null there throws, and the
               throw used to read as «unsupported» and send the clip to seeking. */
            const cfg = (typeof VMW.decoderConfig === 'function') ? VMW.decoderConfig(d) : {
                codec: d.codec, description: d.desc,
                codedWidth: d.vtrack.video.width, codedHeight: d.vtrack.video.height,
            };
            const sup = await window.VideoDecoder.isConfigSupported(cfg);
            if (!sup || !sup.supported) return null;
        } catch (_) { return null; }

        let stream, cursor;
        try {
            /* Слайдшоу рисует кадр клипа теми же функциями, что и фото
               (Ken Burns, «целиком», карточка стиля, размытый фон), — обычным
               drawImage, поэтому получает кадр, повёрнутый по-настоящему
               (ClipCursor → VideoOrientation.upright). До 20.10.2026 в Firefox
               клип с телефона уходил в файл боком и вписывался полосой 16:9
               (обращение 19.10, колода 9:16). /video-editor рисует через
               VideoOrientation сам и просит deferRotation. */
            stream = new VMW.DecodeStream(d, Math.max(0, (startSec || 0)) * 1e6, null, { deferRotation: !!deferRotation });
            cursor = new VMW.ClipCursor(stream, fps);
        } catch (_) { return null; }

        return {
            engine: 'webcodecs',
            rotationDeg: d.rotationDeg || 0,
            /* display-, а НЕ coded-размер: рядом живёт seek-движок, который
               отдаёт v.videoWidth/videoHeight (браузер уже применил матрицу
               tkhd). Пока здесь стоял coded-размер, ОДИН и тот же клип с
               телефона выходил стоймя или боком в зависимости от того, какой
               движок выиграл, — и воспроизвести это в отчёте было нечем. */
            width: d.displayWidth || d.vtrack.video.width,
            height: d.displayHeight || d.vtrack.video.height,
            frameAt: function (tSec) { return cursor.frameAt(Math.max(0, tSec) * 1e6); },
            close: function () { try { cursor.close(); } catch (_) {} },
        };
    }

    function loadSeekElement(file) {
        const v = document.createElement('video');
        v.src = URL.createObjectURL(file);
        v.preload = 'auto';
        v.muted = true;
        v.playsInline = true;
        v.style.cssText = 'position:fixed;left:0;bottom:0;width:2px;height:2px;opacity:.01;pointer-events:none';
        document.body.appendChild(v);
        return new Promise(function (res) {
            let settled = false;
            const fin = function (x) { if (!settled) { settled = true; res(x); } };
            v.onloadeddata = function () { fin(true); };
            v.onerror = function () { fin(false); };
            setTimeout(function () { fin(!!v.videoWidth); }, 20000);
        }).then(function (ok) {
            v.onloadeddata = null; v.onerror = null;
            if (!ok) { releasePreviewElement(v); return null; }
            return v;
        });
    }

    /* ── A seek that never lands must not hold the export ────────────────
       frameAt waited for `seeked` with no limit and no ear for `error`. On
       21.10.2026 (comment #1978, Yandex Browser, Windows) a WebM slide stood
       still for three minutes of VISIBLE time in two exports of the same deck —
       the encoder awaits each frame, so the whole export stood with it until
       the stall guard ended it as «Экспорт не удался. Уменьшите разрешение…»,
       advice that had nothing to do with the clip. Her tab had been in the
       background in both runs, which is where a browser parks a paused,
       off-screen player.
       Now a seek has SEEK_LEASH_MS of visible time (a hidden tab is allowed not
       to decode: __visibleTimeout does not count it). A seek that does not land
       or errors gets one fresh <video> — a parked or dead player does not come
       back by itself — and if that fails too the engine says so: the slide
       shows its poster from here on (render-canvas: clip_frame_failed) and the
       export goes on. A normal seek is 50–600 ms (measured above). */
    const SEEK_LEASH_MS = 8000;
    function leash(fn, ms) {
        if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
        const h = setTimeout(fn, ms);
        return function () { clearTimeout(h); };
    }
    function seekLanded(v, t) {
        return new Promise(function (res) {
            let settled = false, stop = null;
            const fin = function (r) {
                if (settled) return;
                settled = true;
                if (stop) { stop(); stop = null; }
                v.removeEventListener('seeked', onSeeked);
                v.removeEventListener('error', onError);
                res(r);
            };
            // The seek landed: the presented-frame wait always ends (timer
            // backstop), so the leash is only for the seek itself.
            const onSeeked = function () { if (stop) { stop(); stop = null; } waitPresented(v, function () { fin('ok'); }); };
            const onError = function () { fin('error'); };
            v.addEventListener('seeked', onSeeked);
            v.addEventListener('error', onError);
            stop = leash(function () { stop = null; fin('stuck'); }, SEEK_LEASH_MS);
            try { v.currentTime = t; } catch (_) { fin('ok'); }
        });
    }

    async function openSeekEngine(file, slug) {
        let v = await loadSeekElement(file);
        if (!v) return null;
        /* closed — the export is over (finished, cancelled, its guard settled it)
           while a seek was still out: a rescue after that would open a <video>
           nobody closes. */
        let last = -1, rebuilt = false, dead = null, closed = false;
        return {
            engine: 'seek',
            width: v.videoWidth,
            height: v.videoHeight,
            frameAt: async function (tSec) {
                if (dead) throw dead;
                const t = Math.max(0, Math.min(isFinite(v.duration) ? v.duration - 0.001 : tSec, tSec));
                // Half-frame tolerance: the encoder often asks for a time the
                // element is already showing (e.g. a clip slower than the deck
                // fps), and a redundant seek is the expensive operation here.
                if (last >= 0 && Math.abs(t - last) < 0.001) return v;
                let r = await seekLanded(v, t);
                if (r !== 'ok' && !closed) {
                    const first = r;
                    if (!rebuilt) {
                        rebuilt = true;
                        const fresh = await loadSeekElement(file);
                        if (fresh && closed) releasePreviewElement(fresh);
                        else if (fresh) { releasePreviewElement(v); v = fresh; last = -1; r = await seekLanded(v, t); }
                    }
                    ssmTrack('clip_seek_stalled', {
                        why: first, rescued: r === 'ok' ? 1 : 0, at: Math.round(t * 10) / 10,
                        w: v.videoWidth || 0, h: v.videoHeight || 0, ext: extOf(file), mb: mbOf(file),
                        hidden: document.hidden ? 1 : 0,
                    }, slug);
                    if (r !== 'ok') {
                        dead = new Error('seek-' + r);
                        dead.code = 'CLIP_SEEK_' + String(r).toUpperCase();
                        throw dead;
                    }
                }
                if (r !== 'ok') throw new Error('seek-closed');
                last = t;
                return v;
            },
            close: function () { closed = true; releasePreviewElement(v); },
        };
    }

    /**
     * Open the best export engine for this clip. Never throws: a null return
     * means "no engine could read it", and the caller renders the slide's
     * poster frame instead of failing the whole export.
     *
     * BOTH engines take `frameAt(tSec)` in ABSOLUTE clip time — decoded frames
     * keep their original timestamps, so `startSec` only tells the WebCodecs
     * engine where to begin feeding samples (a trimmed clip must not decode
     * everything before its in-point), never what the times mean.
     */
    /* opts.slug — чья это телеметрия: модулем пользуется и /video-editor
       (видео поверх видео), и его выбор движка не должен считаться слайдшоу. */
    async function openForExport(file, fps, startSec, opts) {
        let src = null;
        try { src = await openWebCodecsEngine(file, fps, startSec, opts && opts.deferRotation, opts && opts.slug); } catch (_) { src = null; }
        if (!src) {
            try { src = await openSeekEngine(file, opts && opts.slug); } catch (_) { src = null; }
        }
        /* rot — поворот из матрицы файла (съёмка с телефона), up — кадр
           поворачивается копией при выдаче (движок без VideoFrame({rotation})):
           класс обращения 19.10 виден в поле, а не по догадке. */
        if (src) {
            const rot = src.rotationDeg || 0, VO = window.VideoOrientation;
            const up = (rot && !(opts && opts.deferRotation) && VO && VO.native && !VO.native()) ? 1 : 0;
            ssmTrack('clip_export_engine', { engine: src.engine, rot: rot, up: up, ext: extOf(file) }, opts && opts.slug);
        }
        return src;
    }

    window.SlideshowClipSource = {
        isVideoFile: isVideoFile,
        probe: probe,
        probeLadder: probeLadder,
        placeholderPoster: placeholderPoster,
        createPreviewElement: createPreviewElement,
        releasePreviewElement: releasePreviewElement,
        openForExport: openForExport,
        waitPresented: waitPresented,
    };
})();
